package com.fantasytenis.web.di

import com.fantasytenis.shared.core.network.createHttpClient
import com.fantasytenis.shared.core.utils.SharedPrefs
import com.fantasytenis.shared.data.auth.api.AuthApi
import com.fantasytenis.shared.data.auth.repository.AuthRepositoryImpl
import com.fantasytenis.shared.data.tennis.api.TennisApi
import com.fantasytenis.shared.data.tennis.repository.TennisRepositoryImpl
import com.fantasytenis.shared.data.lineup.api.LineupApi
import com.fantasytenis.shared.data.lineup.repository.LineupRepositoryImpl
import com.fantasytenis.shared.data.bracket.api.BracketApi
import com.fantasytenis.shared.data.bracket.repository.BracketRepositoryImpl
import com.fantasytenis.shared.data.survivor.api.SurvivorApi
import com.fantasytenis.shared.data.survivor.repository.SurvivorRepositoryImpl
import com.fantasytenis.shared.data.contests.api.ContestsApi
import com.fantasytenis.shared.data.contests.repository.ContestsRepositoryImpl
import com.fantasytenis.shared.domain.auth.usecases.LoginUseCase
import com.fantasytenis.shared.domain.auth.usecases.RegisterUseCase
import com.fantasytenis.shared.domain.auth.usecases.LogoutUseCase
import com.fantasytenis.shared.domain.auth.usecases.GetCurrentUserUseCase
import com.fantasytenis.shared.domain.tennis.usecases.*
import com.fantasytenis.shared.domain.lineup.usecases.*
import com.fantasytenis.shared.domain.bracket.usecases.*
import com.fantasytenis.shared.domain.survivor.usecases.*
import com.fantasytenis.shared.domain.contests.usecases.*
import com.fantasytenis.shared.data.admin.api.AdminApi
import com.fantasytenis.shared.domain.admin.usecases.GetPlatformConfigUseCase
import com.fantasytenis.shared.domain.admin.usecases.UpdatePlatformConfigUseCase
import com.fantasytenis.shared.data.skills.api.SkillsApi
import com.fantasytenis.shared.data.skills.repository.SkillsRepositoryImpl
import com.fantasytenis.shared.domain.skills.usecases.*
import com.fantasytenis.shared.data.engagement.api.EngagementApi
import com.fantasytenis.shared.data.engagement.repository.EngagementRepositoryImpl
import com.fantasytenis.shared.domain.engagement.usecases.*
import com.fantasytenis.shared.data.notifications.api.NotificationsApi
import com.fantasytenis.shared.data.notifications.repository.NotificationsRepositoryImpl
import com.fantasytenis.shared.domain.notifications.usecases.*
import com.fantasytenis.shared.core.realtime.RealtimeConnection
import com.fantasytenis.shared.core.realtime.JsRealtimeConnection
import com.fantasytenis.web.config.Environment
import io.ktor.client.*

/**
 * Dependency Container for Web platform.
 *
 * Provides singleton instances of all dependencies.
 * This follows the manual DI pattern.
 *
 * URLs are configured at build time:
 * - Production build: Uses https://api.baselinefantasy.com
 * - Development build: Uses http://localhost:50040
 */
object DependencyContainer {

    // Configuration - injected at build time via webpack DefinePlugin
    private val BASE_URL: String get() = Environment.apiBaseUrl
    private val REALTIME_URL: String get() = Environment.realtimeUrl

    // Core
    val sharedPrefs: SharedPrefs by lazy { SharedPrefs() }
    val httpClient: HttpClient by lazy { createHttpClient(BASE_URL, sharedPrefs) }

    // Realtime
    val realtimeConnection: RealtimeConnection by lazy { JsRealtimeConnection(REALTIME_URL) }

    // Auth
    val authApi: AuthApi by lazy { AuthApi(httpClient, BASE_URL) }
    val authRepository: AuthRepositoryImpl by lazy { AuthRepositoryImpl(authApi) }

    // Tennis
    val tennisApi: TennisApi by lazy { TennisApi(httpClient, BASE_URL) }
    val tennisRepository: TennisRepositoryImpl by lazy { TennisRepositoryImpl(tennisApi) }

    // Lineup
    val lineupApi: LineupApi by lazy { LineupApi(httpClient, BASE_URL) }
    val lineupRepository: LineupRepositoryImpl by lazy { LineupRepositoryImpl(lineupApi) }

    // Auth Use Cases
    val loginUseCase: LoginUseCase by lazy {
        LoginUseCase(authRepository, sharedPrefs)
    }
    val registerUseCase: RegisterUseCase by lazy {
        RegisterUseCase(authRepository, sharedPrefs)
    }
    val logoutUseCase: LogoutUseCase by lazy {
        LogoutUseCase(authRepository, sharedPrefs)
    }
    val getCurrentUserUseCase: GetCurrentUserUseCase by lazy {
        GetCurrentUserUseCase(authRepository)
    }

    // Tennis Use Cases
    val getPlayersUseCase: GetPlayersUseCase by lazy {
        GetPlayersUseCase(tennisRepository)
    }
    val getPlayerUseCase: GetPlayerUseCase by lazy {
        GetPlayerUseCase(tennisRepository)
    }
    val getLiveMatchesUseCase: GetLiveMatchesUseCase by lazy {
        GetLiveMatchesUseCase(tennisRepository)
    }
    val getRecentlyCompletedMatchesUseCase: GetRecentlyCompletedMatchesUseCase by lazy {
        GetRecentlyCompletedMatchesUseCase(tennisRepository)
    }
    val getUpcomingMatchesUseCase: GetUpcomingMatchesUseCase by lazy {
        GetUpcomingMatchesUseCase(tennisRepository)
    }
    val getMatchStatisticsUseCase: GetMatchStatisticsUseCase by lazy {
        GetMatchStatisticsUseCase(tennisRepository)
    }

    // Lineup Use Cases
    val createLineupUseCase: CreateLineupUseCase by lazy {
        CreateLineupUseCase(lineupRepository)
    }
    val updateLineupUseCase: UpdateLineupUseCase by lazy {
        UpdateLineupUseCase(lineupRepository)
    }
    val getLineupUseCase: GetLineupUseCase by lazy {
        GetLineupUseCase(lineupRepository)
    }
    val getUserLineupsUseCase: GetUserLineupsUseCase by lazy {
        GetUserLineupsUseCase(lineupRepository)
    }
    val validateLineupUseCase: ValidateLineupUseCase by lazy {
        ValidateLineupUseCase(lineupRepository)
    }
    val canAddPlayerUseCase: CanAddPlayerUseCase by lazy {
        CanAddPlayerUseCase(lineupRepository)
    }
    val lockLineupUseCase: LockLineupUseCase by lazy {
        LockLineupUseCase(lineupRepository)
    }

    // Bracket
    val bracketApi: BracketApi by lazy { BracketApi(httpClient, BASE_URL) }
    val bracketRepository: BracketRepositoryImpl by lazy { BracketRepositoryImpl(bracketApi) }

    // Bracket Use Cases
    val getTournamentDrawUseCase: GetTournamentDrawUseCase by lazy {
        GetTournamentDrawUseCase(bracketRepository)
    }
    val listTournamentDrawsUseCase: ListTournamentDrawsUseCase by lazy {
        ListTournamentDrawsUseCase(bracketRepository)
    }
    val getDrawPositionsUseCase: GetDrawPositionsUseCase by lazy {
        GetDrawPositionsUseCase(bracketRepository)
    }
    val getBracketChallengeUseCase: GetBracketChallengeUseCase by lazy {
        GetBracketChallengeUseCase(bracketRepository)
    }
    val listBracketChallengesUseCase: ListBracketChallengesUseCase by lazy {
        ListBracketChallengesUseCase(bracketRepository)
    }
    val createBracketEntryUseCase: CreateBracketEntryUseCase by lazy {
        CreateBracketEntryUseCase(bracketRepository)
    }
    val getBracketEntryUseCase: GetBracketEntryUseCase by lazy {
        GetBracketEntryUseCase(bracketRepository)
    }
    val getUserBracketEntriesUseCase: GetUserBracketEntriesUseCase by lazy {
        GetUserBracketEntriesUseCase(bracketRepository)
    }
    val submitBracketPicksUseCase: SubmitBracketPicksUseCase by lazy {
        SubmitBracketPicksUseCase(bracketRepository)
    }
    val cancelBracketEntryUseCase: CancelBracketEntryUseCase by lazy {
        CancelBracketEntryUseCase(bracketRepository)
    }
    val getBracketLeaderboardUseCase: GetBracketLeaderboardUseCase by lazy {
        GetBracketLeaderboardUseCase(bracketRepository)
    }
    val getFullBracketUseCase: GetFullBracketUseCase by lazy {
        GetFullBracketUseCase(bracketRepository)
    }
    val setLockOverrideUseCase: SetLockOverrideUseCase by lazy {
        SetLockOverrideUseCase(bracketApi)
    }

    // Survivor
    val survivorApi: SurvivorApi by lazy { SurvivorApi(httpClient, BASE_URL) }
    val survivorRepository: SurvivorRepositoryImpl by lazy { SurvivorRepositoryImpl(survivorApi) }

    // Survivor Use Cases
    val getSurvivorContestUseCase: GetSurvivorContestUseCase by lazy {
        GetSurvivorContestUseCase(survivorRepository)
    }
    val listSurvivorContestsUseCase: ListSurvivorContestsUseCase by lazy {
        ListSurvivorContestsUseCase(survivorRepository)
    }
    val enterSurvivorContestUseCase: EnterSurvivorContestUseCase by lazy {
        EnterSurvivorContestUseCase(survivorRepository)
    }
    val getSurvivorEntryUseCase: GetSurvivorEntryUseCase by lazy {
        GetSurvivorEntryUseCase(survivorRepository)
    }
    val getUserSurvivorEntriesUseCase: GetUserSurvivorEntriesUseCase by lazy {
        GetUserSurvivorEntriesUseCase(survivorRepository)
    }
    val rebuySurvivorEntryUseCase: RebuySurvivorEntryUseCase by lazy {
        RebuySurvivorEntryUseCase(survivorRepository)
    }
    val cancelSurvivorEntryUseCase: CancelSurvivorEntryUseCase by lazy {
        CancelSurvivorEntryUseCase(survivorRepository)
    }
    val getAvailablePlayersUseCase: GetAvailablePlayersUseCase by lazy {
        GetAvailablePlayersUseCase(survivorRepository)
    }
    val makeSurvivorPickUseCase: MakeSurvivorPickUseCase by lazy {
        MakeSurvivorPickUseCase(survivorRepository)
    }
    val updateSurvivorPickUseCase: UpdateSurvivorPickUseCase by lazy {
        UpdateSurvivorPickUseCase(survivorRepository)
    }
    val getSurvivorLeaderboardUseCase: GetSurvivorLeaderboardUseCase by lazy {
        GetSurvivorLeaderboardUseCase(survivorRepository)
    }
    val getSurvivorRoundSummaryUseCase: GetSurvivorRoundSummaryUseCase by lazy {
        GetSurvivorRoundSummaryUseCase(survivorRepository)
    }
    val getSurvivorContestDetailUseCase: GetSurvivorContestDetailUseCase by lazy {
        GetSurvivorContestDetailUseCase(survivorRepository)
    }
    val getUserSurvivorDashboardUseCase: GetUserSurvivorDashboardUseCase by lazy {
        GetUserSurvivorDashboardUseCase(survivorRepository)
    }
    val setSurvivorLockOverrideUseCase: SetSurvivorLockOverrideUseCase by lazy {
        SetSurvivorLockOverrideUseCase(survivorApi)
    }

    // Contests
    val contestsApi: ContestsApi by lazy { ContestsApi(httpClient, BASE_URL) }
    val contestsRepository: ContestsRepositoryImpl by lazy { ContestsRepositoryImpl(contestsApi) }

    // Admin
    val adminApi: AdminApi by lazy { AdminApi(httpClient, BASE_URL) }

    // Contests Use Cases
    val getContestUseCase: GetContestUseCase by lazy {
        GetContestUseCase(contestsRepository)
    }
    val listContestsUseCase: ListContestsUseCase by lazy {
        ListContestsUseCase(contestsRepository)
    }
    val enterContestUseCase: EnterContestUseCase by lazy {
        EnterContestUseCase(contestsRepository)
    }
    val cancelContestEntryUseCase: CancelContestEntryUseCase by lazy {
        CancelContestEntryUseCase(contestsRepository)
    }
    val getUserContestEntriesUseCase: GetUserContestEntriesUseCase by lazy {
        GetUserContestEntriesUseCase(contestsRepository)
    }
    val getContestLeaderboardUseCase: GetContestLeaderboardUseCase by lazy {
        GetContestLeaderboardUseCase(contestsRepository)
    }
    val getSlateUseCase: GetSlateUseCase by lazy {
        GetSlateUseCase(contestsRepository)
    }
    val getSlatePlayersUseCase: GetSlatePlayersUseCase by lazy {
        GetSlatePlayersUseCase(contestsRepository)
    }
    val setContestLockOverrideUseCase: SetContestLockOverrideUseCase by lazy {
        SetContestLockOverrideUseCase(contestsApi)
    }
    val adminDeleteContestEntryUseCase: AdminDeleteContestEntryUseCase by lazy {
        AdminDeleteContestEntryUseCase(contestsApi)
    }
    val adminDeleteBracketEntryUseCase: AdminDeleteBracketEntryUseCase by lazy {
        AdminDeleteBracketEntryUseCase(bracketApi)
    }
    val adminDeleteSurvivorEntryUseCase: AdminDeleteSurvivorEntryUseCase by lazy {
        AdminDeleteSurvivorEntryUseCase(survivorApi)
    }

    // Admin Use Cases
    val getPlatformConfigUseCase: GetPlatformConfigUseCase by lazy {
        GetPlatformConfigUseCase(adminApi)
    }
    val updatePlatformConfigUseCase: UpdatePlatformConfigUseCase by lazy {
        UpdatePlatformConfigUseCase(adminApi)
    }

    // Skills
    val skillsApi: SkillsApi by lazy { SkillsApi(httpClient, BASE_URL) }
    val skillsRepository: SkillsRepositoryImpl by lazy { SkillsRepositoryImpl(skillsApi) }

    // Skills Use Cases
    val getSkillLeaderboardUseCase: GetSkillLeaderboardUseCase by lazy {
        GetSkillLeaderboardUseCase(skillsRepository)
    }
    val getPlayerSkillHistoryUseCase: GetPlayerSkillHistoryUseCase by lazy {
        GetPlayerSkillHistoryUseCase(skillsRepository)
    }
    val getTrendingPlayersUseCase: GetTrendingPlayersUseCase by lazy {
        GetTrendingPlayersUseCase(skillsRepository)
    }
    val getHiddenGemsUseCase: GetHiddenGemsUseCase by lazy {
        GetHiddenGemsUseCase(skillsRepository)
    }

    // Engagement
    val engagementApi: EngagementApi by lazy { EngagementApi(httpClient, BASE_URL) }
    val engagementRepository: EngagementRepositoryImpl by lazy { EngagementRepositoryImpl(engagementApi) }

    // Engagement Use Cases
    val getEngagementUseCase: GetEngagementUseCase by lazy {
        GetEngagementUseCase(engagementRepository)
    }
    val getAchievementsUseCase: GetAchievementsUseCase by lazy {
        GetAchievementsUseCase(engagementRepository)
    }
    val useStreakSaverUseCase: UseStreakSaverUseCase by lazy {
        UseStreakSaverUseCase(engagementRepository)
    }

    // Notifications
    val notificationsApi: NotificationsApi by lazy { NotificationsApi(httpClient, BASE_URL) }
    val notificationsRepository: NotificationsRepositoryImpl by lazy { NotificationsRepositoryImpl(notificationsApi) }

    // Notifications Use Cases
    val getNotificationsUseCase: GetNotificationsUseCase by lazy {
        GetNotificationsUseCase(notificationsRepository)
    }
    val getUnreadCountUseCase: GetUnreadCountUseCase by lazy {
        GetUnreadCountUseCase(notificationsRepository)
    }
    val markNotificationReadUseCase: MarkNotificationReadUseCase by lazy {
        MarkNotificationReadUseCase(notificationsRepository)
    }
    val markAllNotificationsReadUseCase: MarkAllNotificationsReadUseCase by lazy {
        MarkAllNotificationsReadUseCase(notificationsRepository)
    }
    val deleteNotificationUseCase: DeleteNotificationUseCase by lazy {
        DeleteNotificationUseCase(notificationsRepository)
    }
}
